import { JOURS, adressePrincipale, urlPartage } from './contract/index.js';
import { montantBrut } from './formats.js';
const JOURS_SCHEMA = {
    lundi: 'https://schema.org/Monday',
    mardi: 'https://schema.org/Tuesday',
    mercredi: 'https://schema.org/Wednesday',
    jeudi: 'https://schema.org/Thursday',
    vendredi: 'https://schema.org/Friday',
    samedi: 'https://schema.org/Saturday',
    dimanche: 'https://schema.org/Sunday',
};
function joursDeLaPlage(du, au) {
    const debut = JOURS.indexOf(du);
    const fin = JOURS.indexOf(au);
    const sortie = [];
    for (let i = debut;; i = (i + 1) % JOURS.length) {
        const jour = JOURS[i];
        if (jour !== undefined)
            sortie.push(JOURS_SCHEMA[jour]);
        if (i === fin || sortie.length > JOURS.length)
            break;
    }
    return sortie;
}
function adressePostale(galerie) {
    const adresse = adressePrincipale(galerie);
    return {
        '@type': 'PostalAddress',
        streetAddress: [adresse.rue, adresse.complement].filter(Boolean).join(', '),
        postalCode: adresse.codePostal,
        addressLocality: adresse.ville,
        addressCountry: adresse.pays,
    };
}
/** `ArtGallery` : identité du lieu, horaires, adresse, réseaux. */
export function artGallery(ctx) {
    const { galerie } = ctx.siteData;
    const adresse = adressePrincipale(galerie);
    const horaires = galerie.horaires.plages.flatMap((plage) => plage.creneaux.map((creneau) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: joursDeLaPlage(plage.du, plage.au),
        opens: creneau.ouverture,
        closes: creneau.fermeture,
    })));
    const donnees = {
        '@context': 'https://schema.org',
        '@type': 'ArtGallery',
        name: galerie.nom,
        url: ctx.absolu(ctx.lienRubrique('accueil')),
        description: ctx.t(galerie.description),
        email: galerie.email,
        address: adressePostale(galerie),
        openingHoursSpecification: horaires,
    };
    if (galerie.telephone !== undefined)
        donnees['telephone'] = galerie.telephone;
    if (adresse.latitude !== undefined && adresse.longitude !== undefined) {
        donnees['geo'] = {
            '@type': 'GeoCoordinates',
            latitude: adresse.latitude,
            longitude: adresse.longitude,
        };
    }
    if (galerie.reseaux.length > 0)
        donnees['sameAs'] = galerie.reseaux.map((r) => r.url);
    return donnees;
}
function vendeur(ctx) {
    return { '@type': 'ArtGallery', name: ctx.siteData.galerie.nom };
}
/** `VisualArtwork` : le cartel, et l'offre seulement si le prix est public. */
export function visualArtwork(ctx, oeuvre) {
    const artiste = ctx.artiste(oeuvre.artiste);
    const donnees = {
        '@context': 'https://schema.org',
        '@type': 'VisualArtwork',
        name: oeuvre.titre,
        url: ctx.absolu(ctx.lienOeuvre(oeuvre)),
        artMedium: ctx.t(oeuvre.technique),
    };
    if (artiste !== undefined)
        donnees['creator'] = { '@type': 'Person', name: artiste.nom };
    if (oeuvre.annee !== undefined)
        donnees['dateCreated'] = String(oeuvre.annee);
    if (oeuvre.description !== undefined)
        donnees['description'] = ctx.t(oeuvre.description);
    if (oeuvre.dimensions !== undefined && !('texte' in oeuvre.dimensions)) {
        donnees['height'] = {
            '@type': 'QuantitativeValue',
            value: oeuvre.dimensions.hauteur,
            unitCode: 'CMT',
        };
        donnees['width'] = {
            '@type': 'QuantitativeValue',
            value: oeuvre.dimensions.largeur,
            unitCode: 'CMT',
        };
    }
    if (oeuvre.medias.length > 0) {
        donnees['image'] = oeuvre.medias.map((media) => ctx.absolu(urlPartage(media)));
    }
    if (oeuvre.prixVisible && oeuvre.prix !== undefined) {
        donnees['offers'] = {
            '@type': 'Offer',
            price: montantBrut(oeuvre.prix),
            priceCurrency: oeuvre.prix.devise,
            availability: oeuvre.statut === 'disponible'
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
            seller: vendeur(ctx),
            url: ctx.absolu(ctx.lienOeuvre(oeuvre)),
        };
    }
    return donnees;
}
/** `ExhibitionEvent` : dates, lieu, organisateur, stand pour une foire. */
export function exhibitionEvent(ctx, exposition) {
    const nomLieu = exposition.stand === undefined
        ? exposition.lieu.nom
        : `${exposition.lieu.nom} — ${ctx.l.stand} ${exposition.stand}`;
    const donnees = {
        '@context': 'https://schema.org',
        '@type': 'ExhibitionEvent',
        name: ctx.t(exposition.titre),
        url: ctx.absolu(ctx.lienExposition(exposition)),
        startDate: exposition.dateDebut,
        endDate: exposition.dateFin,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: {
            '@type': 'Place',
            name: nomLieu,
            address: {
                '@type': 'PostalAddress',
                streetAddress: exposition.lieu.adresse ?? '',
                addressLocality: exposition.lieu.ville,
                addressCountry: exposition.lieu.pays,
            },
        },
        organizer: vendeur(ctx),
    };
    if (exposition.texte !== undefined)
        donnees['description'] = ctx.t(exposition.texte);
    const premiere = exposition.vuesAccrochage[0];
    if (premiere !== undefined)
        donnees['image'] = [ctx.absolu(urlPartage(premiere))];
    const artistes = ctx.artistesDeLExposition(exposition);
    if (artistes.length > 0) {
        donnees['performer'] = artistes.map((a) => ({ '@type': 'Person', name: a.nom }));
    }
    return donnees;
}
/** `NewsArticle` pour une actualité : utile au flux et aux agrégateurs. */
export function newsArticle(ctx, actualite) {
    const donnees = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: ctx.t(actualite.titre),
        datePublished: actualite.date,
        description: ctx.t(actualite.chapo),
        url: ctx.absolu(ctx.lienActualite(actualite)),
        publisher: vendeur(ctx),
    };
    if (actualite.media !== undefined) {
        donnees['image'] = [ctx.absolu(urlPartage(actualite.media))];
    }
    return donnees;
}
//# sourceMappingURL=jsonld.js.map